import { makeStyles } from '@material-ui/styles'
import { useEffect, useMemo, useRef, useState } from 'react'
import ApiClient from 'api-client/ApiClient'
import type { ImageTask } from 'api-client/Glance'
import { troubleshootImageLibraryDoc } from 'app/docLinks'
import ExternalLink from 'core/components/ExternalLink'
import LoadingTextWithSpinner from 'core/components/LoadingTextWithSpinner'
import Badge, { type BadgeVariant } from 'core/elements/badge/Badge'
import type Theme from 'core/themes/model'
import { capitalizeString } from 'utils/misc'

const IMPORT_TASK_TYPE = 'api_image_import'
const { glance } = ApiClient.getInstance()

// IMGIMP-FR-002: Glance keeps every import attempt as a task. Only the newest one counts, so
// a retry that worked hides an older failure, and a download still running is not a failure.
// created_at is an ISO timestamp, so plain string comparison orders it correctly.
export const isImportFailed = (tasks: ImageTask[] = []): boolean => {
  const imports = tasks.filter((task) => task.type === IMPORT_TASK_TYPE)
  if (!imports.length) return false
  const newest = imports.reduce((a, b) => (b.created_at > a.created_at ? b : a))
  return newest.status === 'failure'
}

interface ImageWithStatus {
  id: string
  status?: string
}

export interface ImageImportStatus {
  // Queued images whose last URL import failed
  failedIds: Set<string>
  // Queued images whose lookup has not answered yet
  checkingIds: Set<string>
}

// IMGIMP-FR-001: when a URL import fails, Glance quietly puts the image back to "queued", so
// only queued images can be hiding a failure. Look up their tasks once and remember the answer:
// - an image newly queued is looked up; images already answered are not asked again
// - an image that leaves "queued" (for example a retry) is forgotten, so it is checked again
//   if it comes back
// - a lookup that errors counts as "not failed" so the status column never breaks
// IMGIMP-FR-005: until its answer arrives, a queued image is reported as "checking", so the
// page never flashes "queued" for an image that is about to show "import failed".
// ponytail: one request per newly queued image; batch or cap if a project ever holds many
// queued images.
export const useImageImportStatus = (images: ImageWithStatus[] = []): ImageImportStatus => {
  const answers = useRef(new Map<string, boolean>())
  const [answered, setAnswered] = useState<Map<string, boolean>>(() => new Map())
  // The images array is a new object on every list refresh; a string key changes only when the
  // set of queued images does, so the effect below does not re-run (and cancel lookups) per poll.
  const queuedKey = images
    .filter((image) => image?.status === 'queued')
    .map((image) => image.id)
    .join(',')
  const queuedIds = useMemo(() => (queuedKey ? queuedKey.split(',') : []), [queuedKey])

  useEffect(() => {
    const queued = new Set(queuedIds)
    const left = [...answers.current.keys()].filter((id) => !queued.has(id))
    if (left.length) {
      for (const id of left) answers.current.delete(id)
      setAnswered(new Map(answers.current))
    }
    const toLookUp = queuedIds.filter((id) => !answers.current.has(id))
    if (!toLookUp.length) return undefined
    let cancelled = false
    Promise.allSettled(toLookUp.map((id) => glance.getImageTasks(id))).then((results) => {
      if (cancelled) return
      toLookUp.forEach((id, i) => {
        const result = results[i]
        answers.current.set(id, result.status === 'fulfilled' && isImportFailed(result.value))
      })
      setAnswered(new Map(answers.current))
    })
    return () => {
      cancelled = true
    }
  }, [queuedIds])

  return useMemo(
    () => ({
      failedIds: new Set(queuedIds.filter((id) => answered.get(id))),
      checkingIds: new Set(queuedIds.filter((id) => !answered.has(id))),
    }),
    [answered, queuedIds],
  )
}

// IMGIMP-FR-004: badge colour per Glance image status (shared by the Images list and details).
const statusVariants: Record<string, BadgeVariant> = {
  active: 'success',
  queued: 'warning',
  saving: 'warning',
  importing: 'warning',
  deactivated: 'warning',
  killed: 'error',
  deleted: 'error',
  pending_delete: 'error',
}

export const getImageStatusVariant = (status: string): BadgeVariant =>
  statusVariants[status] ?? 'unknown'

// Glance statuses are lowercase with underscores; show them in title case for display only
// ("pending_delete" becomes "Pending Delete"). Search and filters still use the raw value.
export const formatImageStatus = (status = ''): string =>
  status.split('_').map(capitalizeString).join(' ')

// A custom tooltip body gets no padding from Tooltip, so it sets its own (same as the standard
// tooltip) and keeps the link readable on the dark background.
const useStyles = makeStyles<Theme>((theme: Theme) => ({
  tooltipBody: {
    padding: theme.spacing(1, 1.5),
    maxWidth: 320,
    color: theme.components.tooltip.text,
  },
  // SimpleLink paints both the link and its inner text span primary blue, which is unreadable on
  // the dark tooltip; the doubled selector outranks its colour class.
  tooltipLink: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    textDecoration: 'underline',
    '&&, && .simple-link-text': {
      color: theme.components.tooltip.text,
    },
  },
}))

// The backend saves the same generic "Internal Error" text for every failed download, so the
// tooltip gives plain guidance instead of that message. Once the backend records the real
// reason (PCD-10452), show it here.
const ImportFailedTooltip = () => {
  const classes = useStyles()
  return (
    <div className={classes.tooltipBody}>
      The Image Library host could not download the image from the URL. Check that the URL is
      correct and that the host can reach it (proxy or firewall settings).
      <ExternalLink url={troubleshootImageLibraryDoc} className={classes.tooltipLink}>
        Troubleshooting guide
      </ExternalLink>
    </div>
  )
}

interface ImageStatusBadgeProps {
  status: string
  importFailed?: boolean
  checking?: boolean
}

// IMGIMP-FR-003 / IMGIMP-FR-004 / IMGIMP-FR-005
export const ImageStatusBadge = ({
  status,
  importFailed = false,
  checking = false,
}: ImageStatusBadgeProps) => {
  if (importFailed) {
    return <Badge text="Import Failed" variant="error" tooltipBody={<ImportFailedTooltip />} />
  }
  if (checking) return <LoadingTextWithSpinner />
  return <Badge text={formatImageStatus(status)} variant={getImageStatusVariant(status)} />
}
